/*
 * KxExplorerEngine — pure calculation engine for the TWT-11 Explorer section.
 *
 * No DOM, no Chart.js, no external deps. Ported from the JS math in
 * savings-estimator-v2.liquid (CSV parsing, kept/exported energy & money) and
 * circle-sweep-v2.liquid (swept-area scaling, power-curve interpolation), plus
 * the solar seasonal shape from winter-gap-graph.liquid. See
 * NextGen/TWT-11-EXPLORER-PLAN.md sections 4-5 for the design this implements.
 *
 * Exposed as window.KxExplorerEngine in a browser, and via module.exports in
 * CommonJS/Node (no DOM access happens at load time, so `require()` is safe
 * outside a browser).
 *
 * Load-profile / production-scale extension (2026-09-23):
 * compute() can derive hour-by-hour own consumption from a household load
 * profile instead of a flat self-use %, and can scale the dataset's measured
 * production to a visitor-set yearly production. Default behaviour (flat
 * self-use %, production scale 1) is unchanged bit-for-bit.
 *
 * New state fields (STATE_DEFAULTS):
 *   loadMode        'flat' (default, existing behaviour) | 'profile'.
 *   annualLoadKwh   Visitor's yearly consumption, kWh. Used only in profile
 *                   mode; clamped >= 0.
 *   loadProfile     Key into config.loadProfiles (default 'h0').
 *   productionScale Multiplies each row's derated kwh/value on the measured-
 *                   production path only (NOT the power-curve/bins/area/
 *                   cut-in path, which stays dataset-based). Clamped to
 *                   [0.05, 5].
 *
 * New config field (DEFAULT_CONFIG.loadProfiles): named { hourly[24],
 * monthly[12], weekend[24] } shapes, keyed by profile id (default 'h0' —
 * BDEW Standardlastprofil H0). hourly x monthly x weekend (applied Sat/Sun
 * only) gives each row's raw load weight; weights are normalized to sum to
 * 1 across the dataset, indexed by LOCAL (CET/CEST) hour/month/weekday via
 * cetOffsetHours().
 *
 * New result fields:
 *   annual.selfUsePct        sumKept / annualKwh x 100 (== selfUse x 100 in
 *                             flat mode).
 *   annual.loadKwh            annualLoadKwh in profile mode, else null.
 *   annual.loadCoveredPct     sumKept / annualLoadKwh x 100 in profile mode
 *                             (annualLoadKwh > 0), else null.
 *   annual.keptPricePerKwh    sumKeptValue / sumKept (0 if sumKept <= 0).
 *   annual.exportPricePerKwh  sumExportedValue / sumExported (0 if <= 0).
 *   annual.meanConsumerPrice  mean of the hourly consumer price column.
 *   annual.meanExportPrice    mean of the hourly export (sold) price column.
 *   monthly.load               12 monthly load sums (zeros in flat mode).
 *   meta.loadMode, meta.loadProfile, meta.productionScale, meta.annualLoadKwh.
 */
'use strict';

(function () {

  // ---------------------------------------------------------------------
  // Constants ported from the two source sections (do not re-derive).
  // ---------------------------------------------------------------------

  // circle-sweep-v2.liquid ~L860: TWT-11's real power curve, [windSpeed, kW].
  var POWER_CURVE = [
    [0, 0],
    [3.0, 0],
    [4.0, 1.0],
    [5.0, 2.1],
    [6.0, 3.3],
    [7.0, 4.3],
    [8.0, 4.5],
    [14.5, 4.5],
    [15.0, 4.0],
    [16.0, 2.8],
    [17.0, 1.5],
    [18.0, 0],
    [30.0, 0]
  ];

  // winter-gap-graph.liquid ~L435: fraction of annual solar yield per month,
  // derived from 2024 Holstebro GHI x system efficiency (0.82), normalized
  // per kWp. Order is Jan..Dec (0-based month index).
  var SOLAR_SHAPE = [
    0.0164, 0.0271, 0.0599, 0.1055, 0.1665, 0.1746,
    0.1549, 0.1375, 0.0905, 0.0438, 0.0161, 0.0071
  ];

  // BDEW Standardlastprofil H0 (VDEW 1999), ported from TWT-11 Installation
  // Checker load-profiles.js (H0_HOURLY, H0_MONTHLY, h0WeekendAdj). `weekend`
  // is h0WeekendAdj expressed as a per-hour multiplier array (applied on
  // Saturday and Sunday only) so this stays plain JSON-serializable data
  // (deepFreeze-safe) instead of a function.
  var LOAD_PROFILES = {
    h0: {
      hourly:  [0.022, 0.018, 0.016, 0.015, 0.016, 0.020, 0.030, 0.048, 0.052, 0.046, 0.042, 0.044, 0.050, 0.046, 0.040, 0.038, 0.044, 0.060, 0.072, 0.068, 0.058, 0.050, 0.040, 0.030],
      monthly: [1.25, 1.18, 1.05, 0.92, 0.80, 0.72, 0.70, 0.74, 0.88, 1.02, 1.18, 1.28],
      weekend: [1, 1, 1, 1, 1, 1, 0.7, 0.7, 1.15, 1.15, 1.15, 1.15, 1.15, 1.15, 1.15, 1.15, 1, 1, 1, 1, 1, 1, 1, 1]
    }
  };

  var DEFAULT_CONFIG = {
    availability: 0.9,
    sweptAreaTwt: 89,
    baselineDiameter: 11,
    powerCurve: POWER_CURVE,
    listPrice: { dk: 165000, de: 22100 },
    serviceCost: { dk: 4500, de: 600 },
    serviceInterval: 2,
    firstPaidServiceYear: 3,
    majorServiceYear: 10,
    majorServiceCost: { dk: null, de: null },
    solarShape: SOLAR_SHAPE,
    solarYieldPerKwp: 853,
    winterMonths: [9, 10, 11, 0, 1, 2], // Oct..Mar, 0-based
    currency: {
      dk: { symbol: 'kr', position: 'after' },
      de: { symbol: '€', position: 'after' }
    },
    loadProfiles: LOAD_PROFILES
  };

  var STATE_DEFAULTS = {
    country: 'dk',
    selfUse: 0.8,
    year: 2024,
    priceMode: 'dataset',
    customPrice: null,
    trend: 0,
    lifetime: 20,
    competitorDiameter: 5.5,
    solarMode: 'match',
    loadMode: 'flat',
    annualLoadKwh: 12000,
    loadProfile: 'h0',
    productionScale: 1
  };

  var LOCALE = { dk: 'da-DK', de: 'de-DE' };
  var CURRENCY_SYMBOL = { dk: 'kr', de: '€' };

  // ---------------------------------------------------------------------
  // Small utilities
  // ---------------------------------------------------------------------

  function num(v, fallback) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    return isFinite(n) ? n : (fallback || 0);
  }

  function clamp(v, lo, hi) {
    return Math.min(hi, Math.max(lo, v));
  }

  function zeros(n) {
    var out = new Array(n);
    for (var i = 0; i < n; i++) out[i] = 0;
    return out;
  }

  function isPlainObject(v) {
    return !!v && typeof v === 'object' && !Array.isArray(v);
  }

  function deepClone(o) {
    if (Array.isArray(o)) return o.map(deepClone);
    if (isPlainObject(o)) {
      var out = {};
      for (var k in o) {
        if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = deepClone(o[k]);
      }
      return out;
    }
    return o;
  }

  function deepFreeze(o) {
    if (isPlainObject(o) || Array.isArray(o)) {
      Object.freeze(o);
      Object.keys(o).forEach(function (k) { deepFreeze(o[k]); });
    }
    return o;
  }

  // One-level-deep merge for nested config maps (listPrice, serviceCost,
  // majorServiceCost, currency); arrays and scalars are replaced wholesale.
  function mergeConfig(config) {
    var out = deepClone(DEFAULT_CONFIG);
    if (!isPlainObject(config)) return out;
    for (var k in config) {
      if (!Object.prototype.hasOwnProperty.call(config, k)) continue;
      var v = config[k];
      if (isPlainObject(v) && isPlainObject(out[k])) {
        for (var kk in v) {
          if (Object.prototype.hasOwnProperty.call(v, kk)) out[k][kk] = v[kk];
        }
      } else {
        out[k] = v;
      }
    }
    return out;
  }

  function mergeState(state) {
    var out = {};
    for (var k in STATE_DEFAULTS) out[k] = STATE_DEFAULTS[k];
    if (isPlainObject(state)) {
      for (var kk in state) {
        if (Object.prototype.hasOwnProperty.call(state, kk) && state[kk] !== undefined) {
          out[kk] = state[kk];
        }
      }
    }
    // Coerce the load-profile / production-scale fields here (rather than in
    // compute(), where the other STATE_DEFAULTS fields are coerced) per the
    // extension spec: loadMode is 'profile' only for that exact string
    // (anything else, including typos, falls back to 'flat').
    out.loadMode = out.loadMode === 'profile' ? 'profile' : 'flat';
    out.annualLoadKwh = clamp(num(out.annualLoadKwh, 12000), 0, Infinity);
    out.productionScale = clamp(num(out.productionScale, 1), 0.05, 5);
    out.loadProfile = typeof out.loadProfile === 'string' ? out.loadProfile : 'h0';
    return out;
  }

  // Ported verbatim from circle-sweep-v2.liquid getInterpolatedPower():
  // "scale from zero" — effective wind speed = windSpeed / stretchFactor,
  // then linear-interpolate on the real TWT-11 power curve. Cut-in and rated
  // speed shift with stretchFactor; NOT multiplied by `availability` here,
  // because circle-sweep-v2 never applies the 0.9 derate to this curve model
  // (only savings-estimator-v2 applies it, and only to the measured CSV kWh
  // column). We replicate that asymmetry intentionally — see area.* in
  // compute() and the REPORT for the parity lane.
  function interpolatePower(windSpeed, stretchFactor, curve) {
    var stretch = stretchFactor || 1;
    var effectiveWs = windSpeed / stretch;
    var last = curve[curve.length - 1];
    if (effectiveWs >= last[0]) return last[1];
    for (var i = 0; i < curve.length - 1; i++) {
      var p1 = curve[i], p2 = curve[i + 1];
      if (effectiveWs >= p1[0] && effectiveWs <= p2[0]) {
        var ratio = (effectiveWs - p1[0]) / (p2[0] - p1[0]);
        return p1[1] + ratio * (p2[1] - p1[1]);
      }
    }
    return 0;
  }

  // Cut-in wind speed for the wind-speed-bin story (area.compCutIn,
  // bins[].*): the curve is exactly 0 at its last zero-power breakpoint and
  // ramps linearly to the next (positive) breakpoint, so the true cut-in is
  // the open interval's lower bound (3.0 m/s for POWER_CURVE). We report the
  // midpoint of that zero -> positive segment (3.0 + 4.0) / 2 = 3.5 m/s,
  // matching the TWT-11 spec sheet's stated 3.5 m/s cut-in rather than the
  // curve's raw (and slightly optimistic) 3.0 m/s edge. ASSUMPTION: this
  // midpoint rule isn't specified upstream in either Liquid section; flagged
  // in the REPORT for the parity lane to confirm against the spec sheet.
  function findCutIn(curve) {
    for (var i = 0; i < curve.length - 1; i++) {
      if (curve[i][1] === 0 && curve[i + 1][1] > 0) {
        return (curve[i][0] + curve[i + 1][0]) / 2;
      }
    }
    return curve[0][0];
  }

  // 1 m/s wind-speed bins: 0, 1, .., 14 as lower edges (bin n covers
  // [n, n+1)), plus one open-ended "15+" bin. 16 bins total.
  function binIndexFor(ws) {
    if (!isFinite(ws) || ws < 0) return 0;
    if (ws >= 15) return 15;
    return Math.floor(ws);
  }

  function binLabels() {
    var labels = [];
    for (var i = 0; i < 15; i++) labels.push(i + '–' + (i + 1));
    labels.push('15+');
    return labels;
  }

  // ---------------------------------------------------------------------
  // Local time (CET/CEST), used only to index the load profile by local
  // hour/month/weekday. DK and DE are both in the same CET/CEST zone.
  // ---------------------------------------------------------------------

  // Day-of-month (UTC) of the last Sunday of a given month (0-based index).
  function lastSundayOfMonth(year, monthIndex) {
    var lastDay = new Date(Date.UTC(year, monthIndex + 1, 0));
    return lastDay.getUTCDate() - lastDay.getUTCDay();
  }

  // CEST (UTC+2) runs from the last Sunday of March 01:00 UTC to the last
  // Sunday of October 01:00 UTC; CET (UTC+1) otherwise.
  function cetOffsetHours(date) {
    var year = date.getUTCFullYear();
    var dstStart = Date.UTC(year, 2, lastSundayOfMonth(year, 2), 1, 0, 0);
    var dstEnd = Date.UTC(year, 9, lastSundayOfMonth(year, 9), 1, 0, 0);
    var t = date.getTime();
    return (t >= dstStart && t < dstEnd) ? 2 : 1;
  }

  // ---------------------------------------------------------------------
  // parseCSV — header-name based column lookup, ported from the
  // findIndex(h => h.startsWith(...) && h.includes(...)) pattern used by
  // both savings-estimator-v2 and circle-sweep-v2's parseCSV().
  // ---------------------------------------------------------------------

  function findCol(headers, prefix, includesStr) {
    for (var i = 0; i < headers.length; i++) {
      if (headers[i].indexOf(prefix) === 0 && headers[i].indexOf(includesStr) !== -1) return i;
    }
    return -1;
  }

  function findColIncludes(headers, str) {
    for (var i = 0; i < headers.length; i++) {
      if (headers[i].indexOf(str) !== -1) return i;
    }
    return -1;
  }

  function parseCSV(text) {
    if (typeof text !== 'string' || !text.trim()) return { rows: [], year: null };

    var lines = text.split(/\r?\n/).filter(function (l) { return l.trim() !== ''; });
    if (lines.length < 2) return { rows: [], year: null };

    var separator = lines[0].indexOf(';') !== -1 ? ';' : ',';
    var headers = lines[0].split(separator).map(function (h) {
      return h.trim().toLowerCase().replace(/\s+/g, '');
    });

    var timeIdx = headers.indexOf('hourutc');
    var col = {
      dk: {
        ws: findCol(headers, 'dk', 'windspeed'),
        kwh: findCol(headers, 'dk', 'power_gen'),
        value: findCol(headers, 'dk', 'value_generated'),
        price: findCol(headers, 'dk', 'consumer_price'),
        sold: findColIncludes(headers, 'dk_sold_to_grid')
      },
      de: {
        ws: findCol(headers, 'de', 'windspeed'),
        kwh: findCol(headers, 'de', 'power_gen'),
        value: findCol(headers, 'de', 'value_generated'),
        price: findCol(headers, 'de', 'consumer_price'),
        sold: findColIncludes(headers, 'de_sold_to_grid')
      }
    };

    function cell(parts, idx) {
      if (idx < 0 || idx >= parts.length) return 0;
      var n = parseFloat(parts[idx]);
      return isFinite(n) ? n : 0;
    }

    var rows = [];
    var year = null;

    for (var li = 1; li < lines.length; li++) {
      var line = lines[li];
      if (line.indexOf(separator) === -1) continue; // skip malformed/empty lines

      var parts = line.indexOf('"') !== -1
        ? line.split(separator).map(function (v) { return v.replace(/['"]+/g, ''); })
        : line.split(separator);

      var t = null;
      if (timeIdx >= 0 && parts[timeIdx]) {
        var raw = String(parts[timeIdx]).trim();
        var iso = raw.indexOf('T') !== -1 ? raw : raw.replace(' ', 'T');
        if (iso.slice(-1) !== 'Z') iso += 'Z';
        var d = new Date(iso);
        if (!isNaN(d.getTime())) t = d;
      }
      if (t && year === null) year = t.getUTCFullYear();

      rows.push({
        t: t,
        dk: {
          ws: cell(parts, col.dk.ws), kwh: cell(parts, col.dk.kwh),
          value: cell(parts, col.dk.value), price: cell(parts, col.dk.price),
          sold: cell(parts, col.dk.sold)
        },
        de: {
          ws: cell(parts, col.de.ws), kwh: cell(parts, col.de.kwh),
          value: cell(parts, col.de.value), price: cell(parts, col.de.price),
          sold: cell(parts, col.de.sold)
        }
      });
    }

    return { rows: rows, year: year };
  }

  function load(url, fetchImpl) {
    var f = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
    if (!f) return Promise.reject(new Error('KxExplorerEngine.load: no fetch implementation available'));
    return f(url).then(function (res) {
      if (!res || !res.ok) throw new Error('KxExplorerEngine.load: failed to fetch ' + url);
      return res.text();
    }).then(parseCSV);
  }

  function defaultConfig() {
    return deepFreeze(deepClone(DEFAULT_CONFIG));
  }

  // ---------------------------------------------------------------------
  // compute — pure, synchronous. Never throws; missing columns / empty
  // dataset / NaN cells all fold to zero.
  // ---------------------------------------------------------------------

  function compute(dataset, state, config) {
    var cfg = mergeConfig(config);
    var st = mergeState(state);
    var country = st.country === 'de' ? 'de' : 'dk';
    var rows = (dataset && Array.isArray(dataset.rows)) ? dataset.rows : [];

    var availability = num(cfg.availability, 0.9);
    var selfUse = clamp(num(st.selfUse, 0.8), 0, 1);
    var priceMode = st.priceMode === 'custom' ? 'custom' : 'dataset';
    var customPrice = num(st.customPrice, 0);
    var trend = num(st.trend, 0);
    var lifetime = Math.max(1, Math.round(num(st.lifetime, 20)));
    var competitorDiameter = num(st.competitorDiameter, 5.5);
    var baselineDiameter = num(cfg.baselineDiameter, 11);
    var sweptAreaTwt = num(cfg.sweptAreaTwt, 89);
    var curve = (Array.isArray(cfg.powerCurve) && cfg.powerCurve.length) ? cfg.powerCurve : DEFAULT_CONFIG.powerCurve;

    // Load-profile / production-scale fields — already coerced by
    // mergeState(): loadMode is 'flat' | 'profile', annualLoadKwh >= 0,
    // productionScale in [0.05, 5], loadProfile a string key.
    var loadMode = st.loadMode;
    var annualLoadKwh = st.annualLoadKwh;
    var productionScale = st.productionScale;
    var loadProfileKey = st.loadProfile;

    // Swept-area scaling, ported from circle-sweep-v2.liquid updateGraph():
    // sliderArea = (d/baselineDiameter)^2 * displayArea; stretch = (displayArea/sliderArea)^(1/3).
    // Guard: a competitor at or above the baseline diameter, or <= 0 m, is
    // treated as "no advantage" (matches the plan's explicit rule for
    // d >= baselineDiameter; the <=0 guard is our own defensive addition,
    // not specified upstream — flagged in the REPORT).
    var doStretch = competitorDiameter > 0 && competitorDiameter < baselineDiameter;
    var competitorArea = doStretch ? Math.pow(competitorDiameter / baselineDiameter, 2) * sweptAreaTwt : sweptAreaTwt;
    var stretch = doStretch ? Math.pow(sweptAreaTwt / competitorArea, 1 / 3) : 1;

    // Cut-in wind speeds for the "starts earlier" story — see findCutIn()
    // comment above for the 3.5 m/s derivation.
    var cutInWs = findCutIn(curve);
    var compCutIn = cutInWs * stretch;

    var monthKwh = zeros(12), monthKept = zeros(12), monthExported = zeros(12);
    var monthKeptValue = zeros(12), monthExportedValue = zeros(12);
    var monthWsSum = zeros(12), monthWsCount = zeros(12);
    var monthTwtCurve = zeros(12), monthCompetitorCurve = zeros(12);

    var sumKwh = 0, sumKept = 0, sumExported = 0, sumKeptValue = 0, sumExportedValue = 0, sumValue = 0;
    var twtCurveTotal = 0, competitorCurveTotal = 0;
    var twtValueTotal = 0, competitorValueTotal = 0;
    var lowWindTwtKwh = 0, hoursBelow8 = 0, hoursAbove8 = 0, priceBelow8Sum = 0, priceAbove8Sum = 0;
    var hoursProducingTwt = 0, hoursProducingComp = 0;

    var binHours = zeros(16), binTwtKwh = zeros(16), binCompKwh = zeros(16);
    var binPriceSum = zeros(16), binTwtValue = zeros(16), binCompValue = zeros(16);

    var monthLoad = zeros(12);
    var priceSum = 0, soldSum = 0, priceRows = 0;

    // Load weights (profile mode only): hourly x monthly x weekend shape for
    // the configured profile, indexed by LOCAL (CET/CEST) hour/month/weekday,
    // normalized so Sum(loadWeights) over the dataset's rows = 1 — so hourly
    // load = loadWeights[i] * annualLoadKwh sums exactly to annualLoadKwh
    // across the dataset. Rows without a valid t (already skipped by the
    // main loop below) get weight 0. Built once, ahead of the per-row loop.
    var loadWeights = zeros(rows.length);
    if (loadMode === 'profile') {
      var profiles = isPlainObject(cfg.loadProfiles) ? cfg.loadProfiles : DEFAULT_CONFIG.loadProfiles;
      var profile = (isPlainObject(profiles[loadProfileKey]) ? profiles[loadProfileKey] : profiles.h0) || DEFAULT_CONFIG.loadProfiles.h0;
      var hourlyShape = (Array.isArray(profile.hourly) && profile.hourly.length === 24) ? profile.hourly : DEFAULT_CONFIG.loadProfiles.h0.hourly;
      var monthlyShape = (Array.isArray(profile.monthly) && profile.monthly.length === 12) ? profile.monthly : DEFAULT_CONFIG.loadProfiles.h0.monthly;
      var weekendShape = (Array.isArray(profile.weekend) && profile.weekend.length === 24) ? profile.weekend : DEFAULT_CONFIG.loadProfiles.h0.weekend;

      var rawWeightSum = 0;
      for (var wi = 0; wi < rows.length; wi++) {
        var wrow = rows[wi];
        if (!wrow || !wrow.t) continue;
        var wOffset = cetOffsetHours(wrow.t);
        var wLocal = new Date(wrow.t.getTime() + wOffset * 3600000);
        var wh = wLocal.getUTCHours();
        var wm = wLocal.getUTCMonth();
        var wd = wLocal.getUTCDay();
        var wRaw = hourlyShape[wh] * monthlyShape[wm] * ((wd === 0 || wd === 6) ? weekendShape[wh] : 1);
        loadWeights[wi] = wRaw;
        rawWeightSum += wRaw;
      }
      if (rawWeightSum > 0) {
        for (var wj = 0; wj < rows.length; wj++) loadWeights[wj] = loadWeights[wj] / rawWeightSum;
      }
    }

    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      if (!row || !row.t) continue;
      var m = row.t.getUTCMonth();
      if (m < 0 || m > 11) continue;

      var side = row[country] || {};
      var ws = num(side.ws, 0);
      var kwhRaw = num(side.kwh, 0);
      var valueRaw = num(side.value, 0);
      var sold = num(side.sold, 0);
      var price = num(side.price, 0);

      // Per-row math ported from savings-estimator-v2.liquid getHourlyValue():
      // both power and value carry the 0.9 availability derate, plus the
      // visitor-set productionScale (measured-production path only — the
      // power-curve/bins/area/cut-in path below stays dataset-based).
      var kwh = kwhRaw * availability * productionScale;
      var value = valueRaw * availability * productionScale;

      var loadH = 0;
      var kept, exported;
      if (loadMode === 'profile') {
        loadH = loadWeights[i] * annualLoadKwh;
        kept = Math.min(kwh, loadH);
        exported = kwh - kept;
      } else {
        kept = kwh * selfUse;
        exported = kwh * (1 - selfUse);
      }

      var keptValue = priceMode === 'custom' ? kept * customPrice
        : loadMode === 'profile' ? kept * price // hourly consumer price column
        : value * selfUse;
      var exportedValue = exported * sold;

      monthKwh[m] += kwh;
      monthKept[m] += kept;
      monthExported[m] += exported;
      monthKeptValue[m] += keptValue;
      monthExportedValue[m] += exportedValue;
      monthWsSum[m] += ws;
      monthWsCount[m] += 1;
      monthLoad[m] += loadH;
      priceSum += price;
      soldSum += sold;
      priceRows += 1;

      sumKwh += kwh; sumKept += kept; sumExported += exported;
      sumKeptValue += keptValue; sumExportedValue += exportedValue; sumValue += value;

      // Area/competitor model (circle-sweep-v2): power-curve based, NOT the
      // measured CSV kWh column, and NOT availability-derated. See note on
      // interpolatePower() above.
      var twtP = interpolatePower(ws, 1, curve);
      monthTwtCurve[m] += twtP;
      twtCurveTotal += twtP;

      var compP = doStretch ? interpolatePower(ws, stretch, curve) : twtP;
      monthCompetitorCurve[m] += compP;
      competitorCurveTotal += compP;

      // Wind-speed bins + value-weighted totals (dk/de consumer price column,
      // per row) for the "starts earlier, earns more when power is
      // expensive" story.
      var bIdx = binIndexFor(ws);
      binHours[bIdx] += 1;
      binTwtKwh[bIdx] += twtP;
      binCompKwh[bIdx] += compP;
      binPriceSum[bIdx] += price;
      binTwtValue[bIdx] += twtP * price;
      binCompValue[bIdx] += compP * price;

      twtValueTotal += twtP * price;
      competitorValueTotal += compP * price;

      if (ws < 8) {
        hoursBelow8 += 1;
        priceBelow8Sum += price;
        lowWindTwtKwh += twtP;
      } else {
        hoursAbove8 += 1;
        priceAbove8Sum += price;
      }

      if (ws >= cutInWs) hoursProducingTwt += 1;
      if (ws >= compCutIn) hoursProducingComp += 1;
    }

    var labels = binLabels();
    var bins = [];
    for (var bj = 0; bj < 16; bj++) {
      bins.push({
        ws: bj === 15 ? 15 : bj,
        label: labels[bj],
        hours: binHours[bj],
        twtKwh: binTwtKwh[bj],
        compKwh: binCompKwh[bj],
        meanPrice: binHours[bj] > 0 ? binPriceSum[bj] / binHours[bj] : 0,
        twtValue: binTwtValue[bj],
        compValue: binCompValue[bj]
      });
    }

    var monthWindSpeed = monthWsCount.map(function (c, idx) {
      return c > 0 ? monthWsSum[idx] / c : 0;
    });

    var annualKwh = sumKwh;
    var meanPricePerKwh = annualKwh > 0 ? sumValue / annualKwh : 0;
    var savings = sumKeptValue + sumExportedValue;

    // Solar reference (solarMode 'match'): size a solar array so its annual
    // yield equals the turbine's annual kWh, shaped by SOLAR_SHAPE.
    var solarShape = (Array.isArray(cfg.solarShape) && cfg.solarShape.length === 12) ? cfg.solarShape : DEFAULT_CONFIG.solarShape;
    var solarYieldPerKwp = num(cfg.solarYieldPerKwp, 853);
    var solarKwp = solarYieldPerKwp > 0 ? annualKwh / solarYieldPerKwp : 0;
    var solarKwh = solarShape.map(function (frac) { return frac * solarYieldPerKwp * solarKwp; });
    var solarTotal = solarKwh.reduce(function (a, b) { return a + b; }, 0);

    var winterMonths = Array.isArray(cfg.winterMonths) ? cfg.winterMonths : DEFAULT_CONFIG.winterMonths;
    var winterKwh = 0, winterSolar = 0;
    winterMonths.forEach(function (mm) {
      if (mm >= 0 && mm <= 11) { winterKwh += monthKwh[mm]; winterSolar += solarKwh[mm]; }
    });
    var winterSharePct = annualKwh > 0 ? (winterKwh / annualKwh) * 100 : 0;
    var solarWinterSharePct = solarTotal > 0 ? (winterSolar / solarTotal) * 100 : 0;

    // area.* — advantagePct = (twt/competitor - 1) x 100, matching
    // circle-sweep-v2's percentGained exactly (advantage/totalSlider x 100).
    var area;
    if (!doStretch) {
      area = {
        twt: twtCurveTotal,
        competitor: twtCurveTotal,
        competitorPctOfTwt: 100,
        competitorAnnualKwh: twtCurveTotal,
        advantagePct: 0
      };
    } else {
      area = {
        twt: twtCurveTotal,
        competitor: competitorCurveTotal,
        competitorPctOfTwt: twtCurveTotal > 0 ? (competitorCurveTotal / twtCurveTotal) * 100 : 0,
        competitorAnnualKwh: competitorCurveTotal,
        advantagePct: competitorCurveTotal > 0 ? ((twtCurveTotal / competitorCurveTotal) - 1) * 100 : 0
      };
    }
    var competitorKwhMonthly = doStretch ? monthCompetitorCurve : monthTwtCurve;

    // Value-weighted area fields. Note competitorValueTotal naturally equals
    // twtValueTotal when !doStretch, because compP === twtP per row in that
    // case (see the loop above) — no separate branch needed here.
    area.compCutIn = compCutIn;
    area.cutIn = cutInWs;
    area.twtValue = twtValueTotal;
    area.competitorValue = competitorValueTotal;
    area.valueAdvantagePct = competitorValueTotal > 0 ? ((twtValueTotal / competitorValueTotal) - 1) * 100 : 0;
    area.lowWindSharePct = twtCurveTotal > 0 ? (lowWindTwtKwh / twtCurveTotal) * 100 : 0;
    area.hoursBelow8 = hoursBelow8;
    area.meanPriceBelow8 = hoursBelow8 > 0 ? priceBelow8Sum / hoursBelow8 : 0;
    area.meanPriceAbove8 = hoursAbove8 > 0 ? priceAbove8Sum / hoursAbove8 : 0;
    area.hoursProducingTwt = hoursProducingTwt;
    area.hoursProducingComp = hoursProducingComp;

    // Budget — ported from TWT-11-EXPLORER-PLAN.md section 4.
    var listPrice = num(cfg.listPrice && cfg.listPrice[country], 0);
    var serviceCost = num(cfg.serviceCost && cfg.serviceCost[country], 0);
    var serviceInterval = Math.max(1, Math.round(num(cfg.serviceInterval, 2)));
    var firstPaidServiceYear = Math.round(num(cfg.firstPaidServiceYear, 3));
    var majorServiceYear = Math.round(num(cfg.majorServiceYear, 10));
    var majorCostRaw = cfg.majorServiceCost && cfg.majorServiceCost[country];
    var majorIncluded = typeof majorCostRaw === 'number' && isFinite(majorCostRaw);
    var majorServiceCost = majorIncluded ? majorCostRaw : 0;

    var years = [];
    var cumulative = -listPrice;
    var totalService = 0;
    var paybackYear = null;

    for (var y = 1; y <= lifetime; y++) {
      var S = savings * Math.pow(1 + trend, y - 1);
      var service = (y >= firstPaidServiceYear && ((y - firstPaidServiceYear) % serviceInterval === 0)) ? serviceCost : 0;
      var major = (y === majorServiceYear && majorIncluded) ? majorServiceCost : 0;
      var net = S - service - major;
      cumulative += net;
      totalService += service + major;
      years.push({ year: y, savings: S, service: service, major: major, net: net, cumulative: cumulative });
      if (paybackYear === null && cumulative >= 0) paybackYear = y;
    }
    var netAfterLifetime = years.length ? years[years.length - 1].cumulative : -listPrice;

    var lifetimeKwh = lifetime * annualKwh;
    var twtPerKwh = lifetimeKwh > 0 ? (listPrice + totalService) / lifetimeKwh : 0;
    var gridPerKwh = meanPricePerKwh;

    var currency = (cfg.currency && cfg.currency[country]) ||
      { symbol: country === 'de' ? '€' : 'kr', position: 'after' };

    return {
      monthly: {
        kwh: monthKwh, kept: monthKept, exported: monthExported,
        keptValue: monthKeptValue, exportedValue: monthExportedValue,
        windSpeed: monthWindSpeed, solarKwh: solarKwh, competitorKwh: competitorKwhMonthly,
        load: monthLoad
      },
      annual: {
        kwh: annualKwh, kept: sumKept, exported: sumExported,
        keptValue: sumKeptValue, exportedValue: sumExportedValue,
        savings: savings, meanPricePerKwh: meanPricePerKwh,
        winterSharePct: winterSharePct, solarWinterSharePct: solarWinterSharePct, solarKwp: solarKwp,
        selfUsePct: annualKwh > 0 ? (sumKept / annualKwh) * 100 : 0,
        loadKwh: loadMode === 'profile' ? annualLoadKwh : null,
        loadCoveredPct: (loadMode === 'profile' && annualLoadKwh > 0) ? (sumKept / annualLoadKwh) * 100 : null,
        keptPricePerKwh: sumKept > 0 ? sumKeptValue / sumKept : 0,
        exportPricePerKwh: sumExported > 0 ? sumExportedValue / sumExported : 0,
        meanConsumerPrice: priceRows > 0 ? priceSum / priceRows : 0,
        meanExportPrice: priceRows > 0 ? soldSum / priceRows : 0
      },
      area: area,
      bins: bins,
      budget: {
        years: years, paybackYear: paybackYear, netAfterLifetime: netAfterLifetime,
        totalService: totalService, majorIncluded: majorIncluded, listPrice: listPrice
      },
      cost: { twtPerKwh: twtPerKwh, gridPerKwh: gridPerKwh, lifetimeKwh: lifetimeKwh },
      meta: {
        country: country,
        year: (dataset && dataset.year != null) ? dataset.year : (st.year || null),
        currency: currency,
        loadMode: loadMode,
        loadProfile: loadProfileKey,
        productionScale: productionScale,
        annualLoadKwh: annualLoadKwh
      }
    };
  }

  // ---------------------------------------------------------------------
  // format — da-DK / de-DE grouped numbers (both use '.' thousands, ','
  // decimal, which is why "20.741 kWh" and "4.200 €" share one style).
  // ---------------------------------------------------------------------

  function format(value, kind, country) {
    var v = (typeof value === 'number' && isFinite(value)) ? value : 0;
    var locale = LOCALE[country] || 'da-DK';

    switch (kind) {
      case 'kwh':
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(v)) + ' kWh';
      case 'money':
        var sym = CURRENCY_SYMBOL[country] || CURRENCY_SYMBOL.dk;
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.round(v)) + ' ' + sym;
      case 'pct':
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(v) + '%';
      case 'kwp':
        return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(v) + ' kWp';
      case 'perkwh':
        var symK = CURRENCY_SYMBOL[country] || CURRENCY_SYMBOL.dk;
        return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' ' + symK + '/kWh';
      case 'ws':
        return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v) + ' m/s';
      default:
        return String(v);
    }
  }

  // ---------------------------------------------------------------------
  // Export
  // ---------------------------------------------------------------------

  var KxExplorerEngine = {
    parseCSV: parseCSV,
    load: load,
    defaultConfig: defaultConfig,
    compute: compute,
    format: format
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = KxExplorerEngine;
  }
  if (typeof window !== 'undefined') {
    window.KxExplorerEngine = KxExplorerEngine;
  }
  if (typeof globalThis !== 'undefined') {
    globalThis.KxExplorerEngine = KxExplorerEngine;
  }

})();

